import { useEffect } from "react";
import { X } from "lucide-react";

/**
 * Drawer — right-side slide-over panel. Used for detail views and builders.
 * Closes on Escape and backdrop click. Token-driven, dark-mode safe.
 */
export default function Drawer({ open, onClose, title, subtitle, icon: Icon, width = 520, footer, children }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <div
      className={`fixed inset-0 z-50 ${open ? "" : "pointer-events-none"}`}
      aria-hidden={!open}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        className="absolute inset-0 transition-opacity duration-300"
        style={{
          background: "rgba(0,0,0,0.45)",
          opacity: open ? 1 : 0,
          backdropFilter: open ? "blur(2px)" : "none",
        }}
      />

      {/* Panel */}
      <div
        className="absolute top-0 right-0 h-full bg-surface border-l border-border flex flex-col shadow-2xl transition-transform duration-300"
        style={{
          width: `min(${width}px, 100%)`,
          transform: open ? "translateX(0)" : "translateX(100%)",
        }}
      >
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border flex-shrink-0">
          {Icon && (
            <div
              className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: "var(--color-primary-tint)", color: "var(--color-primary)" }}
            >
              <Icon className="w-4 h-4" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-text_primary truncate">{title}</h3>
            {subtitle && <p className="text-xs text-text_muted mt-0.5 truncate">{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-text_muted hover:bg-surface_alt hover:text-text_primary transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">{children}</div>

        {footer && (
          <div className="flex-shrink-0 px-5 py-4 border-t border-border bg-surface_alt/40">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
